/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "React Native SDK", description = "Hightouch's React Native SDK for collecting events", lastUpdatedDate = new Date(1710892800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "React Native SDK",
        description: "Hightouch's React Native SDK for collecting events",
        lastUpdatedDate: "Wed Mar 20 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "iOS",
          "depth": 1,
          "id": "ios"
        }, {
          "value": "Android",
          "depth": 1,
          "id": "android"
        }, {
          "value": "SDK Initialization",
          "depth": 0,
          "id": "sdk-initialization"
        }, {
          "value": "Manual tracking API",
          "depth": 0,
          "id": "manual-tracking-api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Reset",
          "depth": 1,
          "id": "reset"
        }, {
          "value": "Out of the box events",
          "depth": 0,
          "id": "out-of-the-box-events"
        }, {
          "value": "Lifecycle events",
          "depth": 1,
          "id": "lifecycle-events"
        }, {
          "value": "Advertising identifiers",
          "depth": 0,
          "id": "advertising-identifiers"
        }, {
          "value": "Android AAID",
          "depth": 1,
          "id": "android-aaid"
        }, {
          "value": "iOS IDFA",
          "depth": 1,
          "id": "ios-idfa"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      code: "code",
      pre: "pre",
      span: "span",
      h3: "h3",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The React Native SDK makes it easy to track events in React Native\napplications. The SDK supports Both iOS and Android applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The React Native SDK is distributed via ", _jsx(_components.code, {
          children: "npm"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Install the required dependencies into your React Native project via ", _jsx(_components.code, {
          children: "npm"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-java",
          children: ["npm install --save \\\n  ", _jsx(_components.span, {
            className: "hljs-meta",
            children: "@ht"
          }), "-sdks/events-sdk-react-", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "native"
          }), " \\\n  ", _jsx(_components.span, {
            className: "hljs-meta",
            children: "@ht"
          }), "-sdks/sovran-react-", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "native"
          }), " \\\n  react-", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "native"
          }), "-get-random-values \\\n  ", _jsx(_components.span, {
            className: "hljs-meta",
            children: "@react"
          }), "-", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "native"
          }), "-async-storage/async-storage\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "ios",
        children: "iOS"
      }), "\n", _jsx(_components.p, {
        children: "On iOS, you will also need to install the native modules by running:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs",
          children: "npx pod-install\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "android",
        children: "Android"
      }), "\n", _jsx(_components.p, {
        children: "The SDK requires additional permissions on Android in order to populate the\ndevice context for events."
      }), "\n", _jsxs(_components.p, {
        children: ["Add these permissions to your app by updating your ", _jsx(_components.code, {
          children: "AndroidManifest.xml"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["<uses-permission android:", _jsx(_components.span, {
            className: "hljs-attr",
            children: "name"
          }), "=", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"android.permission.ACCESS_NETWORK_STATE\""
          }), " />\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "sdk-initialization",
        children: "SDK Initialization"
      }), "\n", _jsx(_components.p, {
        children: "Once the dependencies are installed, you can import the tracking client into\nyour application."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-javascript",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { createClient } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'@ht-sdks/events-sdk-react-native'"
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " htClient = ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "createClient"
          }), "({\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "writeKey"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'API_KEY'"
          }), "\n});\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The client can either be passed around explicitly throughout your app, or\nexposed through a ", _jsx(_components.a, {
          href: "https://react.dev/learn/passing-data-deeply-with-context",
          children: "React context\nhook"
        }), ". To pass the\ncontext through a Context, use ", _jsx(_components.code, {
          children: "AnalyticsProvider"
        }), " and ", _jsx(_components.code, {
          children: "useAnalytics"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-javascript",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " { ", _jsx(_components.span, {
            className: "hljs-title class_",
            children: "AnalyticsProvider"
          }), " } ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "from"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'@ht-sdks/events-sdk-react-native'"
          }), ";\n\n", _jsx(_components.span, {
            className: "hljs-comment",
            children: "// The Child component can retrieve the htClient by calling `useAnalytics()`."
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "const"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "App"
          }), " = (", _jsx(_components.span, {
            className: "hljs-params"
          }), ") => (\n  ", _jsxs(_components.span, {
            className: "xml",
            children: [_jsxs(_components.span, {
              className: "hljs-tag",
              children: ["<", _jsx(_components.span, {
                className: "hljs-name",
                children: "AnalyticsProvider"
              }), " ", _jsx(_components.span, {
                className: "hljs-attr",
                children: "client"
              }), "=", _jsx(_components.span, {
                className: "hljs-string",
                children: "{htClient}"
              }), ">"]
            }), "\n    ", _jsxs(_components.span, {
              className: "hljs-tag",
              children: ["<", _jsx(_components.span, {
                className: "hljs-name",
                children: "Child"
              }), " />"]
            }), "\n  ", _jsxs(_components.span, {
              className: "hljs-tag",
              children: ["</", _jsx(_components.span, {
                className: "hljs-name",
                children: "AnalyticsProvider"
              }), ">"]
            })]
          }), "\n);\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "The client may be configured with the following options:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "writeKey"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The write key used to identify the source in Hightouch."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "collectDeviceId"
              })
            }), _jsx(_components.td, {
              children: "Boolean"
            }), _jsxs(_components.td, {
              children: ["Whether to autocollect a device ID via the Android DRM API. The ID is stored in ", _jsx(_components.code, {
                children: "context.device.id"
              }), " on all events. This is only supported on Android, and is disabled by default."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "debug"
              })
            }), _jsx(_components.td, {
              children: "Boolean"
            }), _jsxs(_components.td, {
              children: ["Whether to generate debug logs. Defaults to ", _jsx(_components.code, {
                children: "true"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "logger"
              })
            }), _jsx(_components.td, {
              children: "Logger"
            }), _jsx(_components.td, {
              children: "Custom logger instance to redirect internal logging from the library."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushAt"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsxs(_components.td, {
              children: ["Maximum number of events to accumulate before sending to the Hightouch API. Defaults to ", _jsx(_components.code, {
                children: "20"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushInterval"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsxs(_components.td, {
              children: ["Maximum number of seconds to hold events before sending to the Hightouch API. Defaults to ", _jsx(_components.code, {
                children: "30"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushPolicies"
              })
            }), _jsx(_components.td, {
              children: "Array"
            }), _jsx(_components.td, {
              children: "Optional advanced customization for how events are buffered before sending to the Hightouch API."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "maxBatchSize"
              })
            }), _jsx(_components.td, {
              children: "Number"
            }), _jsxs(_components.td, {
              children: ["The maximum number of events to send in one HTTP request to the Hightouch API. You may want to adjust this if your events are extremely large. Defaults to ", _jsx(_components.code, {
                children: "1000"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackAppLifecycleEvents"
              })
            }), _jsx(_components.td, {
              children: "Boolean"
            }), _jsxs(_components.td, {
              children: ["Whether to automatically track lifecycle events like ", _jsx(_components.code, {
                children: "Application Installed"
              }), ". Defaults to ", _jsx(_components.code, {
                children: "false"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackDeepLinks"
              })
            }), _jsx(_components.td, {
              children: "Boolean"
            }), _jsxs(_components.td, {
              children: ["Whether to automatically track deep links. Only supported for Android. Defaults to ", _jsx(_components.code, {
                children: "false"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "proxy"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsxs(_components.td, {
              children: ["Used to override the URL used for sending events to Hightouch. This should contain the full path, e.g. ", _jsx(_components.code, {
                children: "https://us-east-1.hightouch-events.com/v1/batch"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "manual-tracking-api",
        children: "Manual tracking API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method is used to send an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["If ", _jsx(_components.code, {
          children: "identify"
        }), " is called multiple times for the same user, the ", _jsx(_components.code, {
          children: "traits"
        }), " are\nmerged together."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htClient", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".identify"
          }), "(userId, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[traits]"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method is used to send a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["htClient.track(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "event"
          }), ", [properties])\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "event"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The event name (for example \"Checked Out\")."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method is used to send a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["htClient", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".screen"
          }), "(name, ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[properties]"
          }), ")\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The screen's name. For example, \"Getting started\"."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsx(_components.td, {
              children: "Additional properties about the event."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["htClient.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(groupId, [traits])\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Object"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "reset",
        children: "Reset"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method resets the ", _jsx(_components.code, {
          children: "identify"
        }), " calls for the local session.\nSpecifically, it resets the ", _jsx(_components.code, {
          children: "anonymousId"
        }), ", ", _jsx(_components.code, {
          children: "userId"
        }), ", and ", _jsx(_components.code, {
          children: "traits"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method should be called when users log out. This way, if the user\nlogs back in with another account, the userIds and traits from the different\nsessions remain separate."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["htClient", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".reset"
          }), "()\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "out-of-the-box-events",
        children: "Out of the box events"
      }), "\n", _jsx(_components.h3, {
        id: "lifecycle-events",
        children: "Lifecycle events"
      }), "\n", _jsxs(_components.p, {
        children: ["When ", _jsx(_components.code, {
          children: "trackAppLifecycleEvents"
        }), " is enabled, Hightouch automatically tracks the following events:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Application Installed"
          }), " -- Emitted when the app is first opened after a new install."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Application Updated"
          }), " -- Emitted when the app is first opened after upgrading from a previous version."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Application Opened"
          }), " -- Emitted whenever the app is opened (including when it's resuming from the background)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Application Backgrounded"
          }), " -- Emitted whenever the app is backgrounded."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "advertising-identifiers",
        children: "Advertising identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["To collect IDFAs and AAIDs for advertising identification, you may use the\n", _jsx(_components.code, {
          children: "@ht-sdks/events-sdk-react-native-plugin-advertising-id"
        }), " and\n", _jsx(_components.code, {
          children: "@ht-sdks/events-sdk-react-native-plugin-idfa"
        }), " packages."]
      }), "\n", _jsx(_components.p, {
        children: "These plugins load native modules that pull the local advertising ID."
      }), "\n", _jsx(_components.h3, {
        id: "android-aaid",
        children: "Android AAID"
      }), "\n", _jsxs(_components.p, {
        children: ["Once setup, the AAID will be stored in ", _jsx(_components.code, {
          children: "context.device.advertisingId"
        })]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Add the Android AAID plugin to your project."
          }), "\n", _jsx(_components.pre, {
            children: _jsxs(_components.code, {
              className: "hljs language-java",
              children: ["npm install ", _jsx(_components.span, {
                className: "hljs-meta",
                children: "@ht"
              }), "-sdks/events-sdk-react-", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "native"
              }), "-plugin-advertising-id\n"]
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Add the plugin to your code by adding the following code after initializing your client."
          }), "\n", _jsx(_components.pre, {
            children: _jsxs(_components.code, {
              className: "hljs language-csharp",
              children: ["htClient.", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "add"
              }), "({plugin: ", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "new"
              }), " AdvertisingIdPlugin()});\n"]
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Update your Android application manifest to include your ", _jsx(_components.a, {
              href: "https://developers.google.com/admob/android/quick-start",
              children: "AdMob app ID"
            }), "."]
          }), "\n", _jsx(_components.pre, {
            children: _jsx(_components.code, {
              className: "hljs language-php-template",
              children: _jsxs(_components.span, {
                className: "xml",
                children: [_jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "manifest"
                  }), ">"]
                }), "\n  ", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "application"
                  }), ">"]
                }), "\n    ", _jsx(_components.span, {
                  className: "hljs-comment",
                  children: "<!-- Sample AdMob app ID: ca-app-pub-3940256099942544~3347511713 -->"
                }), "\n    ", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "meta-data"
                  }), "\n        ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "android:name"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"com.google.android.gms.ads.APPLICATION_ID\""
                  }), "\n        ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "android:value"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"ca-app-pub-xxxxxxxxxxxxxxxx~yyyyyyyyyy\""
                  }), "/>"]
                }), "\n  ", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "application"
                  }), ">"]
                }), "\n", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "manifest"
                  }), ">"]
                }), "\n"]
              })
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "ios-idfa",
        children: "iOS IDFA"
      }), "\n", _jsxs(_components.p, {
        children: ["Once setup, the IDFA will be stored in ", _jsx(_components.code, {
          children: "context.device.advertisingId"
        })]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Add the iOS IDFA plugin to your project."
          }), "\n", _jsx(_components.pre, {
            children: _jsxs(_components.code, {
              className: "hljs language-java",
              children: ["npm install ", _jsx(_components.span, {
                className: "hljs-meta",
                children: "@ht"
              }), "-sdks/events-sdk-react-", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "native"
              }), "-plugin-idfa\n"]
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Add the plugin to your code by adding the following code after initializing your client."
          }), "\n", _jsx(_components.pre, {
            children: _jsxs(_components.code, {
              className: "hljs language-csharp",
              children: ["htClient.", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "add"
              }), "({plugin: ", _jsx(_components.span, {
                className: "hljs-keyword",
                children: "new"
              }), " IdfaPlugin()});\n"]
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Update your ", _jsx(_components.code, {
              children: "Info.plist"
            }), " to set\n", _jsx(_components.a, {
              href: "https://developer.apple.com/documentation/bundleresources/information_property_list/nsusertrackingusagedescription",
              children: "NSUserTrackingUsageDescription"
            }), ".\nThis description will be displayed when prompting users for permission to\ntrack their IDFA."]
          }), "\n"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
